import type { WorkspaceSummary } from "./account-client";

export interface CreatorIdentity {
  id: string;
  name: string;
  email: string;
  image?: string | null;
}

export type ManagementSessionState =
  | { status: "checking" }
  | { status: "ready"; mode: "demo" | "google"; user?: CreatorIdentity; isAdmin: boolean; workspaces?: WorkspaceSummary[] }
  | { status: "link-required"; mode: "demo" }
  | { status: "sign-in-required"; mode: "google" }
  | { status: "access-required"; mode: "google"; user: CreatorIdentity }
  | { status: "failed"; mode?: "demo" | "google" };

interface SessionPayload {
  authenticated?: boolean;
  authorized?: boolean;
  mode?: "demo" | "google";
  user?: CreatorIdentity;
  isAdmin?: boolean;
  workspaces?: WorkspaceSummary[];
}

let managementSessionPromise: Promise<ManagementSessionState> | null = null;

function bootstrapTokenFromFragment(): string | null {
  const fragment = window.location.hash.slice(1);
  if (!fragment) return null;
  const params = new URLSearchParams(fragment);
  return params.get("bootstrap") ?? params.get("token") ?? (fragment.includes("=") ? null : decodeURIComponent(fragment));
}

async function readSession(): Promise<{ response: Response; payload: SessionPayload }> {
  const response = await fetch("/api/demo/manage-session", {
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  const payload = await response.json().catch(() => ({})) as SessionPayload;
  return { response, payload };
}

export function establishManagementSession(): Promise<ManagementSessionState> {
  if (!managementSessionPromise) {
    const request = resolveManagementSession();
    managementSessionPromise = request;
    void request.then((result) => {
      if (result.status === "failed" && managementSessionPromise === request) {
        managementSessionPromise = null;
      }
    });
  }
  return managementSessionPromise;
}

export function resetManagementSessionCache(): void {
  managementSessionPromise = null;
}

async function resolveManagementSession(): Promise<ManagementSessionState> {
  if (import.meta.env.VITE_SKIP_MANAGEMENT_CHECK === "true") {
    return { status: "ready", mode: "demo", isAdmin: false, workspaces: [] };
  }

  const session = await readSession().catch(() => null);
  if (!session) return { status: "failed" };

  if (session.response.ok) {
    return readySession(session.payload);
  }

  if (session.payload.mode === "google") {
    if (session.response.status === 403 && session.payload.user) {
      return { status: "access-required", mode: "google", user: session.payload.user };
    }
    return session.response.status === 401
      ? { status: "sign-in-required", mode: "google" }
      : { status: "failed", mode: "google" };
  }

  const token = bootstrapTokenFromFragment();
  if (!token) return { status: "link-required", mode: "demo" };
  window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);

  const response = await fetch("/api/demo/bootstrap", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      accept: "application/json",
      "content-type": "application/json",
    },
    body: JSON.stringify({ token }),
  }).catch(() => null);

  if (!response?.ok) return { status: "failed", mode: "demo" };
  const confirmed = await readSession().catch(() => null);
  return confirmed?.response.ok
    ? readySession(confirmed.payload)
    : { status: "failed", mode: "demo" };
}

function readySession(payload: SessionPayload): Extract<ManagementSessionState, { status: "ready" }> {
  const ready: Extract<ManagementSessionState, { status: "ready" }> = {
    status: "ready",
    mode: payload.mode ?? "demo",
    isAdmin: payload.isAdmin === true,
    workspaces: Array.isArray(payload.workspaces) ? payload.workspaces : [],
  };
  if (payload.user) ready.user = payload.user;
  return ready;
}
